<template>
  <section
    class="mx-auto px-4 py-1"
    :data-timestamp="event.timestamp"
  >
    <div
      class="mx-auto flex h-[44px] w-fit max-w-full items-center gap-1 rounded-8 bg-bg-base-bg1 px-2 py-1 shadow-raised"
    >
      <wui-dropdown
        v-if="contact"
        direction="top-right"
        close-on-click-outside
        plain
      >
        <template #activator="{ toggle }">
          <wui-icon
            class="cursor-pointer shrink-0"
            :class="iconColorClass"
            :name="iconName"
            @click.stop="toggle()"
          />
        </template>
        <template #default>
          <wui-card
            state="text"
            shadow="overflow"
          >
            <wui-list class="whitespace-nowrap">
              <wui-list-item
                @click="callAudio"
              >
                {{ $t('common.makeCall') }} ({{ contact.internalNumber }})
              </wui-list-item>
              <wui-list-item
                v-if="contact.mobilePhone"
                @click="mobileCall"
              >
                {{ $t('common.makeCallMobile') }} ({{ contact.mobilePhone }})
              </wui-list-item>
            </wui-list>
          </wui-card>
        </template>
      </wui-dropdown>
      <wui-icon
        v-else
        class="shrink-0"
        :class="iconColorClass"
        :name="iconName"
      />

      <div class="min-w-0 text-left whitespace-nowrap">
        <p class="text-label-m-13-18 text-fg-base-fg10">
          {{ directionText }}
        </p>
        <p
          v-if="secondaryText"
          class="text-label-r-13-18"
          :class="isMissed ? 'text-surf-neg-base-def' : 'text-fg-base-fg22'"
        >
          {{ secondaryText }}
        </p>
      </div>
    </div>

    <div
      v-if="isUnread"
      class="relative"
    >
      <span class="absolute right-[-10px] bottom-0 h-2 w-2 rounded-full bg-primary-tints-400" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { IconName, useRenderNotifier, WuiCard, WuiDropdown, WuiIcon, WuiList, WuiListItem } from '@wui/common-library'
import {
  HistoryMessageItem,
  MessageCallMissed,
  MessageCallEnded,
  MessageCallTerminated,
  MessageCallBusy,
  MessageCallForwarded,
} from '@wui/im'
import { computed, toRefs } from 'vue'

import { useChatEvent } from '@/entities/chat-handlers'
import { useStoreContacts } from '@/entities/contact'
import { useUnreadMessages } from '@/entities/messages'
import { useContact } from '@/entities/use-contact'
import { callFromAnotherTerminalIfNeeded } from '@/entities/user-terminal'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import { conversionFromNumberToTime } from '@/shared/utils/date-time'

import { getCallEventText, getPersonalCallStatusText } from '../'

const props = defineProps<{
  event: HistoryMessageItem<MessageCallMissed | MessageCallEnded | MessageCallTerminated | MessageCallBusy | MessageCallForwarded>,
  light: boolean,
  next?: HistoryMessageItem,
}>()

const { event, next } = toRefs(props)

const { currentUser } = useCurrentUser()
const { t } = useLocalization()
const { findByPServed } = useStoreContacts()
const { notifyError } = useRenderNotifier()
const { hhmm } = useChatEvent(event, next)

const isOutgoing = computed(() => event.value.item.srcPServed === currentUser.value?.imLogin)

const otherPServed = computed(() => {
  const item = event.value.item
  if (item.event === 'call_forwarded') return item.pServed
  return isOutgoing.value && 'dstPServed' in item ? item.dstPServed : item.srcPServed
})

const contact = computed(() => findByPServed(otherPServed.value) as Contact)

const isMissed = computed(() => event.value.item.event === 'call_missed')

const isFailedCall = computed(() => {
  const { event: eventType } = event.value.item
  return eventType === 'call_missed'
    || eventType === 'dialing_busy'
    || eventType === 'dialing_terminated'
})

const iconColorClass = computed(() =>
  isFailedCall.value ? 'text-surf-neg-base-def' : 'text-surf-pos-base-def',
)

const iconName = computed<IconName>(() =>
  isOutgoing.value ? 'arrowNorthEastM' : 'arrowSouthWestM',
)

const directionText = computed(() => {
  const key = isOutgoing.value
    ? 'chat.personalCallOutgoingAt'
    : 'chat.personalCallIncomingAt'
  return t(key, { time: hhmm.value })
})

const duration = computed(() => {
  if (event.value.item.event === 'call_forwarded') return ''
  if ((event.value.item as MessageCallEnded).duration) {
    return conversionFromNumberToTime((event.value.item as MessageCallEnded).duration).trim()
  }
  return ''
})

const secondaryText = computed(() => {
  if (event.value.item.event === 'call_forwarded') {
    return getCallEventText(event.value, contact.value?.name)
  }

  if (duration.value) return duration.value

  return getPersonalCallStatusText(event.value, isOutgoing.value)
})

const { audioCall, callMobile } = useContact(contact.value)
const callAudio = async () => {
  try {
    if (contact.value?.imLogin && await callFromAnotherTerminalIfNeeded(contact.value.imLogin)) {
      return
    }

    await audioCall()
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const mobileCall = async () => {
  try {
    if (contact.value?.mobilePhone && await callFromAnotherTerminalIfNeeded(contact.value.mobilePhone)) {
      return
    }

    await callMobile()
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const { isUnread } = useUnreadMessages(props.event)
</script>
